import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, filter, shareReplay, take, tap, finalize } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationStore } from '../stores/configuration.store';
import { UserInterface } from '../interfaces/user.interface';
import { ResourceService } from './resource.service';
import { RolesStore } from '../stores/roles.store';
import { UserStore } from '../stores/user.store';
import { GroupsStore } from '../stores/groups.store';
import { ResourcesEndpointEnum } from '../enums/resources-endpoint.enum';
import { AuthEventsService } from './auth-events.service';
import { FrontendConfig } from '../interfaces/frontend-config';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userSubject = new BehaviorSubject<UserInterface | null>(null);
  public user$ = this.userSubject.asObservable().pipe(shareReplay(1));
  private configuration!: FrontendConfig;
  private userWelcomeMessageShown = false;

  constructor( private rolesStore: RolesStore,
    private resourceService: ResourceService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private userStore: UserStore,
    private groupsStore: GroupsStore,
    private authEventsService: AuthEventsService,
    private toastrService: ToastrService,
    private configurationStore: ConfigurationStore,
    private translateService: TranslateService)
  {
    this.initializeUserData();
    this.getAppName();
    this.authEventsService.unauthorizedError$.subscribe(() => {
      this.handleUnauthorizedError();
    });
    this.authEventsService.forbiddenError$.subscribe(() => {
      this.handleForbiddenError();
    });
  }

  private async initializeUserData(): Promise<void> {
    if (this.isLoggedIn()) {
      this.getUserData().subscribe();
    }
  }

  getAppName() {
    this.configurationStore.config$.pipe(
      filter((config: FrontendConfig | null) => config !== null))
      .subscribe({
        next: (config: FrontendConfig | null) => {
          this.configuration = config!
        },
        error: (err) => {
          throwError(() => err);
        }
      });
  }

  public havePremiumLicense(): Observable<boolean> {
    return this.user$.pipe(
      map(user => !!user?.IsPremium)
    );
  }
  public doesntHavePremiumLicense(): Observable<boolean> {
    return this.user$.pipe(
      map(user => !user?.IsPremium)
    );
  }
  public updatePremiumLicense(isPremium: boolean){
    this.resourceService.get<UserInterface>(ResourcesEndpointEnum.GET_ROLE).subscribe({
      next: (user: UserInterface) => {
        this.userSubject.next(user);
        this.userStore.user = user;
      },
      error: (err: Error) => {
        if(!this.userWelcomeMessageShown) {
          this.userWelcomeMessageShown = true;
          this.translateService.get(['Please contact Admin.', 'Error: Could not authorize account.']).subscribe(t => {
            this.toastrService.error(t['Please contact Admin.'], t['Error: Could not authorize account.']);
          });
        }
        this.userStore.user = null;
        console.error('Error fetching user data: ', err);
        return throwError(() => err);
      },
      complete: () => { window.location.reload(); }
    });
  }

  public isAdmin(): Observable<boolean> {
    return this.user$.pipe(
      map(user => !!user?.GlobalAdmin)
    );
  }
  public isNotAdmin(): Observable<boolean> {
    return this.user$.pipe(
      map(user => !user?.GlobalAdmin)
    );
  }
  public isOwner(): Observable<boolean> {
    return this.user$.pipe(
      map(user => !!user?.OwnerOfGroup)
    );
  }
  public isNotOwner(): Observable<boolean> {
    return this.user$.pipe(
      map(user => !user?.OwnerOfGroup)
    );
  }
  public getUserData(): Observable<UserInterface> {
    var userDataExists = false;
    if (this.userStore.user$) {
      this.userStore.user$.pipe(take(1)).subscribe({
        next: user => {
          if(user){
            userDataExists = true;
            this.userSubject.next(user);
          }
        }
      });
    }

    if(!userDataExists) {
      this.resourceService.get<UserInterface>(ResourcesEndpointEnum.GET_ROLE).subscribe({
        next: (user: UserInterface) => {
          this.userSubject.next(user);
          this.userStore.user = user;
          if(!this.userWelcomeMessageShown) {
            this.userWelcomeMessageShown = true;
            this.translateService.get(['Welcome to the ', 'Hi ']).subscribe(t => {
              this.toastrService.success(t['Welcome to the '] + this.configuration.AppName, t['Hi '] + user.FirstName + '!');
            });
          }
        },
        error: (err: any) => {
          if(!this.userWelcomeMessageShown) {
            this.userWelcomeMessageShown = true;
            this.translateService.get(['Please contact Admin.', 'Error: Could not authorize account.']).subscribe(t => {
              this.toastrService.error(t['Please contact Admin.'], t['Error: Could not authorize account.']);
            });
          }
          this.userStore.user = null;
          console.error('Error fetching user data: ', err);
          return throwError(() => err);
        }
      });
    }

    return this.userStore.user$.pipe(map(user => user!));
  }

  setLoginDisplay(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  public isLoggedIn() {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
    const account = this.msalService.instance.getActiveAccount();
    return !!account;
  }

  public login() {
    this.msalService.initialize().subscribe((x) => {
      this.msalService.instance.loginRedirect();
      this.router.navigate(['/home']);
    })
  }

  public signOut() {
    this.userSubject.next(null);
    this.userStore.user = null;
    this.rolesStore.roles = [];
    this.groupsStore.groups = [];
    this.configurationStore.config = null;

    // handleRedirectPromise is memoized so it won't re-clear stale flags on subsequent calls.
    // The key 'msal.interaction.status' is stored literally (no clientId prefix) in
    // sessionStorage and cookies (storeAuthStateInCookie: true). Remove from all backends.
    const interactionKey = 'msal.interaction.status';
    sessionStorage.removeItem(interactionKey);
    localStorage.removeItem(interactionKey);
    // document.cookie = `${interactionKey}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    this.msalService.instance.logoutRedirect();
  }

  handleAdminGuardRouting(): void {
    this.msalBroadcastService.msalSubject$.pipe(
      take(1),
      tap(() => {
        if (this.setLoginDisplay()) {
          this.router.navigate(['home']);
        }
      })
    ).subscribe();
  }

  private handleUnauthorizedError(): void {
    this.router.navigate(['/sign-out']);
    // this.signOut();
  }

  private handleForbiddenError(): void {
    this.router.navigate(['/home']);
  }
}
